import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { zplRendererWorker } from '../workers/workers';

// --- Interfaces ---
export interface ZplFile {
    zpl: string;
    fileName: string;
}

export interface ZplRenderOptions {
    zpl: string;
    wmm?: number;
    hmm?: number;
    dpmm?: number;
}

export interface ZplZipOptions {
    labels: ZplFile[];
    wmm?: number;
    hmm?: number;
    dpmm?: number;
}

export interface ZplRenderResult {
    ok: boolean;
    b64?: string;
    blob?: Blob;
    error?: string;
}

@Injectable({
    providedIn: 'root',
})
export class ZplRendererService implements OnDestroy {
    private worker: Worker | null;
    private readonly messageSubject = new Subject<{
        id: number;
        result: ZplRenderResult;
    }>();
    private msgId = 0;

    constructor() {
        this.worker = zplRendererWorker();
        if (this.worker) {
            this.worker.onmessage = ({ data }) =>
                this.handleWorkerMessage(data);
        }
    }

    ngOnDestroy(): void {
        this.worker?.terminate();
        this.messageSubject.complete();
    }

    renderZpl(options: ZplRenderOptions): Observable<ZplRenderResult> {
        const id = ++this.msgId;
        this.worker?.postMessage({
            type: 'render',
            id,
            zpl: options.zpl,
            wmm: options.wmm ?? 101.6,
            hmm: options.hmm ?? 50.8,
            dpmm: options.dpmm ?? 8,
        });
        return this.waitForResponse(id);
    }

    createZip(options: ZplZipOptions): Observable<ZplRenderResult> {
        const id = ++this.msgId;
        this.worker?.postMessage({
            type: 'zip',
            id,
            labels: options.labels,
            wmm: options.wmm ?? 101.6,
            hmm: options.hmm ?? 50.8,
            dpmm: options.dpmm ?? 8,
        });
        return this.waitForResponse(id);
    }

    private waitForResponse(id: number): Observable<ZplRenderResult> {
        const response$ = new Subject<ZplRenderResult>();
        const subscription = this.messageSubject.subscribe(
            ({ id: messageId, result }) => {
                if (id === messageId) {
                    response$.next(result);
                    response$.complete();
                    subscription.unsubscribe();
                }
            }
        );
        return response$.asObservable();
    }

    private handleWorkerMessage(data: { id: number } & ZplRenderResult): void {
        this.messageSubject.next({ id: data.id, result: data });
    }
}
